"use client";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import type { ImageItemT } from "@/forApp/types/Entity";
import IconButton from "@/shared/Buttons/IconButton/IconButton";
import { Icons } from "@/shared/Icons/Icons";
import ImageWithLoader from "@/shared/Image/ImageWithLoader";
import styles from "./fullscreen-styles.module.css";

interface FullscreenGalleryProps {
	images: ImageItemT[];
	initialIndex: number;
	onClose: () => void;
}

const FullscreenGallery: React.FC<FullscreenGalleryProps> = ({
	images,
	initialIndex,
	onClose,
}) => {
	const [selectedImageIndex, setSelectedImageIndex] = useState(initialIndex);
	const [mainViewportRef, mainEmbla] = useEmblaCarousel({
		startIndex: initialIndex,
		loop: true,
	});
	const [thumbViewportRef, thumbEmbla] = useEmblaCarousel({
		containScroll: "keepSnaps",
		dragFree: true,
	});

	// Sync the main carousel with the thumbnail carousel
	const onThumbClick = useCallback(
		(index: number) => {
			if (!mainEmbla || !thumbEmbla) return;
			mainEmbla.scrollTo(index);
		},
		[mainEmbla, thumbEmbla],
	);

	// Update selected image index when main carousel scrolls
	const onSelect = useCallback(() => {
		if (!mainEmbla || !thumbEmbla) return;
		const index = mainEmbla.selectedScrollSnap();
		setSelectedImageIndex(index);
		thumbEmbla.scrollTo(index);
	}, [mainEmbla, thumbEmbla]);

	// Scroll to previous slide
	const scrollPrev = useCallback(() => {
		if (mainEmbla) mainEmbla.scrollPrev();
	}, [mainEmbla]);

	// Scroll to next slide
	const scrollNext = useCallback(() => {
		if (mainEmbla) mainEmbla.scrollNext();
	}, [mainEmbla]);

	// Add event listeners
	useEffect(() => {
		if (mainEmbla) {
			mainEmbla.on("select", onSelect);
			onSelect();
		}
	}, [mainEmbla, onSelect]);

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			} else if (e.key === "ArrowLeft") {
				scrollPrev();
			} else if (e.key === "ArrowRight") {
				scrollNext();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [onClose, scrollPrev, scrollNext]);

	return (
		<div className={styles.fullscreenOverlay}>
			<div className={styles.fullscreenContent}>
				<IconButton
					icon={<Icons.Close />}
					onClick={onClose}
					color="gallery"
					className={styles.closeButton}
					ariaLabel="Close fullscreen gallery"
				/>

				<div className={styles.mainCarouselContainer}>
					<div className={styles.emblaViewport} ref={mainViewportRef}>
						<div className={styles.emblaContainer}>
							{images.map((image, index) => (
								<div className={styles.emblaSlide} key={`slide-${index}`}>
									<div className={styles.mainImageContainer}>
										<ImageWithLoader
											src={image.original_path}
											alt={`Фото объекта ${index + 1}`}
											fill
											objectFit="scale-down"
											className={styles.mainImage}
											borderRadius={0}
											priority={index === selectedImageIndex}
											transparent={true}
										/>
									</div>
								</div>
							))}
						</div>
					</div>

					<IconButton
						icon={<Icons.ArrowLeft />}
						onClick={scrollPrev}
						color="gallery"
						className={styles.prevButton}
						ariaLabel="Previous image"
					/>

					<IconButton
						icon={<Icons.ArrowRight />}
						onClick={scrollNext}
						color="gallery"
						className={styles.nextButton}
						ariaLabel="Next image"
					/>
				</div>

				<div className={styles.thumbnailsContainer}>
					<div className={styles.emblaViewport} ref={thumbViewportRef}>
						<div className={styles.emblaContainer}>
							{images.map((image, index) => (
								<div
									className={`${styles.emblaSlide} ${
										index === selectedImageIndex ? styles.activeThumb : ""
									}`}
									key={`thumb-${index}`}
									onClick={() => onThumbClick(index)}
								>
									<div className={styles.thumbnailWrapper}>
										<ImageWithLoader
											src={image.thumbnail_path || image.original_path}
											alt={`Thumbnail ${index + 1}`}
											width={85}
											height={85}
											borderRadius={0}
											objectFit="cover"
											className={styles.thumbnailImage}
										/>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FullscreenGallery;
